import React, { Fragment, useEffect, useState } from 'react'

import { StandardPage } from '../../components/_EOS/StandardPage'
import {
  GenericOrderBy,
  GenericTable,
  getNewDirection
} from '../../components/_EOS/Table'
import { PreSaleSku } from './entities/PreSaleSku'
import { generateTableHeadersPreSaleSku } from '.'
import { ApiResponse } from '../../typings/ApiResponse'
import { useApi } from '../../hooks/useApi'
import { Status } from '../../hooks/useFetchState'

import { getPreSaleSku, postExportPreSaleSkuMasive } from './api'
import { usePagination } from '../../hooks/usePagination'
import { PreSaleSkuSearch } from './PreSaleSkuSearch'
import { defaultFilters } from './config'
import { useModal } from '../../hooks/useModal'
import { EditPreSaleSkuModal } from './modals/EditPreSaleSkuModal'
import { Modal } from '../../components/_EOS/Modal'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { useSnackbar } from '../../hooks/useSnackbar'
import { EditPreSaleSkuMasiveModal } from './modals/EditPreSaleSkuMasiveModal'
import { useProductOrigins } from '../../hooks'
import { Box, Button, Divider, IconButton, Tooltip, Typography } from '@mui/material'
import {
  Edit as EditIcon,
  Download as DownloadIcon,
  Edit as EditAltIcon
} from '@mui/icons-material'
import { Filter } from '../../components/_WMS/FilterComponent'
import { FilterExtraAction } from '../../components/_WMS/FilterComponent/types'
import { Loading } from '../../components/UI'

interface PreSaleSkuPageProps {}

export const PreSaleSkuPage: React.FC<PreSaleSkuPageProps> = () => {
  const { state, callApi } = useApi<ApiResponse<PreSaleSku>>()
  const [selectedElement, setSelectedElement] = React.useState<number[]>([])
  const [orderBy, setOrderBy] = useState<GenericOrderBy<PreSaleSku>>({
    field: '',
    direction: 'desc'
  })
  const [selectedFilters, setSelectedFilters] =
    React.useState<Filter[]>(defaultFilters)

  const pagination = usePagination(5)
  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()

  const tableHeadersPreSaleSku = generateTableHeadersPreSaleSku()

  const requestPreSaleSku = (
    filters?: Filter[],
    page?: number,
    pageSize?: number
  ) => {
    callApi(getPreSaleSku(filters, page, pageSize, orderBy)).then(() =>
      setSelectedElement([])
    )
  }

  const handleSorting = ({ field, direction }: GenericOrderBy<PreSaleSku>) => {
    const auxField = field === 'skuId' ? 'skuId' : field
    if (auxField === orderBy.field) {
      const newDirection = getNewDirection(orderBy.direction)
      setOrderBy((_prev) => ({ field: auxField, direction: newDirection }))
      return
    }

    setOrderBy({ field, direction })
  }

  const handleCheckChange = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedElement((prev) => [...prev, id])
    } else {
      setSelectedElement((prev) => [...prev.filter((el) => el !== id)])
    }
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    // If the pageSize is different, we should set page 0
    const auxPage = pagination.pageSize !== pageSize ? 0 : page

    setSelectedElement([])

    requestPreSaleSku(selectedFilters, auxPage, pageSize)

    pagination.setPagination(auxPage, pageSize)
  }

  useEffect(() => {
    pagination.setPagination(0, pagination.pageSize)

    requestPreSaleSku(selectedFilters, 0, pagination.pageSize)
  }, [orderBy, selectedFilters])

  const editModalState = useModal<{
    entity: PreSaleSku
  }>('')
  const createModalState = useModal('Modificar Stock Venta en Verde')
  const confirmExportModalState = useModal<{}>('Descargar Stock Venta en Verde')

  const { state: StateExport, callApi: callApiExport } = useApi()

  const handleExportPreSaleSkuMasive = () => {
    callApiExport(postExportPreSaleSkuMasive(selectedFilters))
      .then(() => {
        confirmExportModalState.closeModal()
        openSnackBar(`Proceso agendado correctamente`, true)
      })
      .catch((error) => {
        openSnackBar('Error agendando exportación: ' + error, false)
      })
  }

  const { getProductOrigins } = useProductOrigins()

  useEffect(() => {
    getProductOrigins()
  }, [])

  const extraActions: FilterExtraAction[] = [
    {
      label: 'Descargar Stock Venta en Verde',
      onClick: () => confirmExportModalState.openModal('sm'),
      icon: <DownloadIcon />,
      disabled: StateExport.status === Status.Loading
    },
    {
      label: 'Modificar Stock Venta en Verde',
      onClick: () => createModalState.openModal('sm'),
      icon: <EditAltIcon />
    }
  ]

  type CustomActionsProps = {
    id: number
    row: PreSaleSku
  }

  const MyCustomActions: React.FC<CustomActionsProps> = ({ row }) => {
    return (
      <Fragment>
        <Tooltip title='Editar' aria-label='edit'>
          <IconButton
            aria-label='seeBinacle'
            onClick={() =>
              editModalState.openModal(
                'xs',
                { entity: row },
                'Editar Stock Venta en Verde'
              )
            }
            style={{ verticalAlign: 'bottom' }}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
      </Fragment>
    )
  }

  if (StateExport.status === Status.Loading) return <Loading />

  return (
    <Fragment>
      <StandardPage pageTitle=''>
        <PreSaleSkuSearch
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          onSearchClick={requestPreSaleSku}
          extraActions={extraActions}
        />
        {/* TABLE */}
        <GenericTable<PreSaleSku>
          headersConfig={tableHeadersPreSaleSku}
          IDKey={'skuId'}
          data={state.data?.items}
          pagination={{
            totalItem: state.data?.totalItems || 0,
            page: pagination.page,
            pageSize: pagination.pageSize
          }}
          onPaginationChange={handlePaginationChange}
          isLoading={state.status === Status.Loading}
          onCheckChange={handleCheckChange}
          checkedElements={selectedElement}
          onSortChange={handleSorting}
          orderField={orderBy}
          customActionComponent={MyCustomActions}
        />
      </StandardPage>

      <Modal
        open={editModalState.isOpen}
        title={editModalState.title}
        maxWidth={editModalState.size}
        onClose={editModalState.closeModal}
      >
        <EditPreSaleSkuModal
          preSaleSku={editModalState.extendedProps?.entity as PreSaleSku}
          handleSnackbar={openSnackBar}
          handleCloseModal={editModalState.closeModal}
          requestPreSaleSku={() => {
            requestPreSaleSku(
              selectedFilters,
              pagination.page,
              pagination.pageSize
            )
          }}
        />
      </Modal>
      <Modal
        open={createModalState.isOpen}
        title={createModalState.title}
        maxWidth={createModalState.size}
        onClose={createModalState.closeModal}
      >
        <EditPreSaleSkuMasiveModal
          handleSnackbar={openSnackBar}
          handleCloseModal={createModalState.closeModal}
          requestWorkOrderRecall={() => {
            requestPreSaleSku(
              selectedFilters,
              pagination.page,
              pagination.pageSize
            )
          }}
        />
      </Modal>

      <Modal
        onClose={confirmExportModalState.closeModal}
        open={confirmExportModalState.isOpen}
        title={confirmExportModalState.title}
        maxWidth={confirmExportModalState.size}
      >
        <Box>
          <Typography variant='h6' gutterBottom>
            ¿Desea continuar con la descarga?
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>
            La generación del archivo se realizará en segundo plano.
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>
            La descarga estará disponible en el menú <strong>Procesos</strong>.
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>
            Aplique los filtros antes de continuar si desea limitar los
            resultados.
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary', mb: 3 }}>
            Este proceso puede tardar unos minutos.
          </Typography>
          <Divider/>

          <Box display='flex' justifyContent='flex-end' sx={{ gap: 2, mt: 3 }}>
            <Button
              variant='contained'
              color='primary'
              onClick={() => handleExportPreSaleSkuMasive()}
            >
              Aceptar
            </Button>
            <Button
              variant='outlined'
              color='primary'
              onClick={confirmExportModalState.closeModal}
            >
              Cancelar
            </Button>
            
          </Box>
        </Box>
      </Modal>

      {/* SNACKBAR */}
      <SnackBar
        message={message}
        open={isActive}
        status={isSuccess ? 'success' : 'error'}
      />
    </Fragment>
  )
}
